import {
  areSimilar,
  pathIn,
  atPath,
  apply,
  invert,
  mul,
  hitTestPath,
  pickSelectable,
  nodesInRect, nodesInLasso,
  isAutoLayout,
  isContextContainer,
  connectorGeometry,
  distToSegment,
  makeNode,
  solid,
  parseColor,
  STICKY_COLORS,
  normalizeAngle,
  anchorsOf,
  pathCommands,
  moveAnchorPart,
  moveAnchorsBy,
  anchorsInPolygon,
  setVectorPath,
  flatten,
  flattenPath,
  nodeOutline,
  type Mat,
  type NodeData,
  type NodeId,
  type NodeType,
  type Rect,
  type Vec2,
} from '@vellum/core';
import type { App, Guide } from '../app.ts';
import { selectionBox, handlePositions, quickCreateHandles, titleAt, type HandleId, HANDLES } from './overlay.ts';
import { pencilToPath, penToPath } from './paths.ts';
import { widgetClick } from '../widgets.ts';

type Gesture =
  | { kind: 'pan'; start: Vec2; cam: { x: number; y: number } }
  | { kind: 'pending'; start: Vec2; startPage: Vec2; hit: NodeData | null; marqueeInstead: boolean; alt: boolean; shift: boolean }
  | { kind: 'marquee'; startPage: Vec2; base: NodeId[]; shift: boolean; scope: NodeId }
  | { kind: 'lasso'; points: Vec2[]; base: NodeId[]; shift: boolean; scope: NodeId }
  | {
      kind: 'move';
      startPage: Vec2;
      applied: Vec2;
      ids: NodeId[];
      bounds: Rect;
      targets: { xs: number[]; ys: number[]; rects: Rect[] };
      reorderParent: NodeId | null;
      grab: Vec2;
    }
  | {
      kind: 'resize';
      handle: HandleId;
      startPage: Vec2;
      single: NodeData | null;
      startWorld: Mat;
      box: { x: number; y: number; width: number; height: number };
      starts: { id: NodeId; b: Rect; w: number; h: number }[];
    }
  | { kind: 'rotate'; center: Vec2; a0: number; starts: { id: NodeId; rotation: number; center: Vec2 }[] }
  | { kind: 'create'; tool: App['tool']; startPage: Vec2; parent: NodeId; id: NodeId | null; shift: boolean }
  | { kind: 'pencil'; points: Vec2[] }
  | { kind: 'eraser' }
  | { kind: 'pathPoint'; id: NodeId; cmd: number; part: 'p' | 'hin' | 'hout' }
  | { kind: 'pathPoints'; id: NodeId; start: Vec2; starts: { cmd: number; page: Vec2 }[] }
  | { kind: 'pathLasso'; id: NodeId; points: Vec2[]; base: number[]; shift: boolean }
  | { kind: 'cropPan'; id: NodeId; start: Vec2; crop: { x: number; y: number; scale: number } }
  | { kind: 'connector'; startPage: Vec2; startId: NodeId | null }
  | { kind: 'endpoint'; id: NodeId; which: 'connectorStart' | 'connectorEnd' }
  | { kind: 'lineEnd'; id: NodeId; which: 0 | 1; fixed: Vec2 };

const DRAG_THRESHOLD = 3;

export class Interaction {
  gesture: Gesture | null = null;
  private lastClick = { t: 0, x: 0, y: 0 };
  private penPoints: { p: Vec2; out?: Vec2 }[] = [];
  private penDragging = false;

  constructor(
    private app: App,
    private el: HTMLElement,
  ) {}

  private get doc() {
    return this.app.doc;
  }
  private get editor() {
    return this.app.editor;
  }

  private local(e: PointerEvent | MouseEvent | WheelEvent): Vec2 {
    const r = this.el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  /** Is there an anchor or handle of the vector being edited under this screen point? */
  private anchorAt(s: Vec2) {
    const n = this.app.ui.editingPathId ? this.doc.get(this.app.ui.editingPathId) : null;
    if (!n) return false;
    const m = mul(this.app.viewMatrix(), this.doc.worldTransform(n.id));
    return anchorsOf(pathCommands(n.path)).some((a) => [a.p, a.hin, a.hout].some((pt) => pt && Math.hypot(apply(m, pt).x - s.x, apply(m, pt).y - s.y) <= 6));
  }

  private tol() {
    return 4 / this.app.camera.zoom;
  }

  // ------------------------------------------------------------------ pointer

  pointerDown(e: PointerEvent) {
    const app = this.app;
    if (app.ui.contextMenu) app.setUI({ contextMenu: null });
    const s = this.local(e);
    const p = app.screenToPage(s);
    // grab an existing ruler guide
    if (app.tool === 'select' && app.ui.showRulers) {
      const gs = this.doc.get(this.editor.pageId)?.guides ?? [];
      const i = gs.findIndex((g) => Math.abs((g.axis === 'x' ? app.pageToScreen({ x: g.pos, y: 0 }).x - s.x : app.pageToScreen({ x: 0, y: g.pos }).y - s.y)) <= 3);
      if (i >= 0) {
        app.setUI({ draggingGuide: { ...gs[i], index: i } });
        this.el.setPointerCapture(e.pointerId);
        return;
      }
    }
    this.el.setPointerCapture(e.pointerId);

    if (app.ui.followId) app.setUI({ followId: null }); // taking control stops following
    if (e.button === 1 || app.ui.spaceDown || app.tool === 'hand') {
      this.gesture = { kind: 'pan', start: s, cam: { x: app.camera.x, y: app.camera.y } };
      this.el.style.cursor = 'grabbing';
      return;
    }
    if (e.button === 2) return;
    if (app.ui.editingTextId) {
      app.setUI({ editingTextId: null });
      if (app.tool !== 'select') return;
    }
    // view-only: select, pan and comment; nothing that edits
    if ((app.readOnly || app.ui.devMode) && !['select', 'hand', 'comment'].includes(app.tool)) {
      app.setTool('select');
      return;
    }

    switch (app.tool) {
      case 'select':
        return this.selectDown(e, s, p);
      case 'lasso':
        if (app.ui.editingPathId && this.doc.get(app.ui.editingPathId)) {
          // vector edit mode: grab a point as usual, otherwise lasso points
          if (this.anchorAt(s)) return this.selectDown(e, s, p);
          const base = e.shiftKey ? [...app.ui.pathAnchors] : [];
          this.gesture = { kind: 'pathLasso', id: app.ui.editingPathId, points: [p], base, shift: e.shiftKey };
          app.setUI({ lasso: [p], ...(e.shiftKey ? {} : { pathAnchor: null, pathAnchors: [] }) });
          return;
        }
        this.gesture = { kind: 'lasso', points: [p], base: e.shiftKey ? [...this.editor.selection] : [], shift: e.shiftKey, scope: app.ui.scopeId ?? this.editor.pageId };
        if (!e.shiftKey) this.editor.setSelection([]);
        app.setUI({ lasso: [p] });
        return;
      case 'pencil':
        this.gesture = { kind: 'pencil', points: [p] };
        app.setUI({ draft: { kind: 'pencil', points: [p] } });
        return;
      case 'eraser':
        this.editor.beginGesture('Erase');
        this.gesture = { kind: 'eraser' };
        this.erase(p);
        return;
      case 'stamp': {
        const size = 56;
        const id = this.editor.create('TEXT', { name: 'Stamp', characters: app.toolOptions.stamp, fontSize: size, x: p.x - size * 0.6, y: p.y - size * 0.6, textAutoResize: 'WIDTH_AND_HEIGHT', pluginData: { stamp: app.userName } });
        this.editor.setSelection([id]);
        return;
      }
      case 'mindmap': {
        const id = this.editor.createMindMap(p.x - 80, p.y - 24);
        this.editor.setSelection([id]);
        app.setTool('select');
        app.setUI({ editingTextId: id });
        return;
      }
      case 'pen':
        return this.penDown(p);
      case 'connector': {
        const start = this.connectable(p);
        this.gesture = { kind: 'connector', startPage: p, startId: start?.id ?? null };
        app.setUI({ draft: { kind: 'connector', points: [p], startId: start?.id, cursor: p } });
        return;
      }
      case 'image':
        this.pickImage(p);
        return;
      case 'comment': {
        // Anchor to the node under the pointer so the pin follows it.
        const path = hitTestPath(this.doc, this.editor.pageId, p, { tolerance: this.tol() });
        const target = path[path.length - 1];
        if (target) {
          const b = this.doc.worldBounds(target.id);
          app.setUI({ commentDraft: { x: p.x - b.x, y: p.y - b.y, nodeId: target.id }, openCommentId: null });
        } else app.setUI({ commentDraft: { x: p.x, y: p.y }, openCommentId: null });
        return;
      }
      default: {
        if (app.tool === 'text') {
          // clicking on a shape's outline with the text tool puts text on that path
          const shape = this.pathNear(p);
          if (shape) {
            const id = this.editor.textOnPath(shape, 'Type something', {}, p);
            app.setTool('select');
            app.setUI({ editingTextId: id });
            return;
          }
        }
        const parent = this.dropTarget(p, new Set()) ?? this.editor.pageId;
        this.gesture = { kind: 'create', tool: app.tool, startPage: p, parent, id: null, shift: e.shiftKey };
      }
    }
  }

  pointerMove(e: PointerEvent): void {
    const app = this.app;
    const s = this.local(e);
    const p = app.screenToPage(s);
    app.sync?.presence({ cursor: p });
    app.lastPointer = p;
    const dg = app.ui.draggingGuide;
    if (dg) {
      app.setUI({ draggingGuide: { ...dg, pos: Math.round(dg.axis === 'x' ? p.x : p.y) } });
      return;
    }
    const g = this.gesture;
    if (g && (app.readOnly || app.ui.devMode) && !['pan', 'marquee', 'pending'].includes(g.kind)) return;
    if (!g) {
      this.hover(e, s, p);
      if (app.tool === 'pen' && this.penPoints.length) app.setUI({ draft: { ...(app.ui.draft ?? { kind: 'pen', points: [] }), cursor: p } });
      return;
    }
    switch (g.kind) {
      case 'pan':
        app.setCamera({ ...app.camera, x: g.cam.x + s.x - g.start.x, y: g.cam.y + s.y - g.start.y });
        return;
      case 'pending': {
        if (Math.hypot(s.x - g.start.x, s.y - g.start.y) < DRAG_THRESHOLD) return;
        if (g.marqueeInstead || !g.hit) {
          const scope = g.hit && g.marqueeInstead ? g.hit.id : (app.ui.scopeId ?? this.editor.pageId);
          this.gesture = { kind: 'marquee', startPage: g.startPage, base: g.shift ? [...this.editor.selection] : [], shift: g.shift, scope };
          if (!g.shift) this.editor.setSelection([]);
        } else {
          this.startMove(g.startPage, g.alt);
        }
        return this.pointerMove(e);
      }
      case 'lasso': {
        const last = g.points[g.points.length - 1];
        // a point every couple of screen pixels keeps the path smooth without piling up points
        if (Math.hypot(p.x - last.x, p.y - last.y) * app.camera.zoom < 2) return;
        g.points.push(p);
        app.setUI({ lasso: [...g.points] });
        const hits = nodesInLasso(this.doc, g.scope, g.points).map((n) => n.id);
        this.editor.setSelection(g.shift ? [...new Set([...g.base, ...hits])] : hits);
        return;
      }
      case 'pathLasso': {
        const last = g.points[g.points.length - 1];
        if (Math.hypot(p.x - last.x, p.y - last.y) * app.camera.zoom < 2) return;
        g.points.push(p);
        const hits = anchorsInPolygon(this.doc, g.id, g.points);
        const all = g.shift ? [...new Set([...g.base, ...hits])] : hits;
        app.setUI({ lasso: [...g.points], pathAnchors: all, pathAnchor: all.length === 1 ? all[0] : null });
        return;
      }
      case 'pathPoints':
        moveAnchorsBy(this.editor, g.id, g.starts, { x: p.x - g.start.x, y: p.y - g.start.y });
        return;
      case 'marquee': {
        const r = rectFrom(g.startPage, p);
        app.setUI({ marquee: r });
        const hits = nodesInRect(this.doc, g.scope, r).map((n) => n.id);
        this.editor.setSelection(g.shift ? [...new Set([...g.base, ...hits])] : hits);
        return;
      }
      case 'move':
        return this.moveDrag(g, p, e);
      case 'resize':
        return this.resizeDrag(g, p, e);
      case 'rotate': {
        let delta = (Math.atan2(p.y - g.center.y, p.x - g.center.x) - g.a0) * (180 / Math.PI);
        if (e.shiftKey) delta = Math.round(delta / 15) * 15;
        this.editor.transact('Rotate', () => {
          for (const st of g.starts) {
            const n = this.doc.get(st.id);
            if (!n) continue;
            const rad = (delta * Math.PI) / 180;
            const dx = st.center.x - g.center.x,
              dy = st.center.y - g.center.y;
            const nc = { x: g.center.x + dx * Math.cos(rad) - dy * Math.sin(rad), y: g.center.y + dx * Math.sin(rad) + dy * Math.cos(rad) };
            const lc = n.parent && this.doc.get(n.parent)?.type !== 'PAGE' ? this.doc.toLocal(n.parent, nc) : nc;
            this.editor.update(st.id, { rotation: normalizeAngle(st.rotation + delta), x: lc.x - n.width / 2, y: lc.y - n.height / 2 }, 'Rotate');
          }
        });
        return;
      }
      case 'create':
        return this.createDrag(g, p, e);
      case 'pencil':
        g.points.push(p);
        app.setUI({ draft: { kind: 'pencil', points: g.points } });
        return;
      case 'eraser':
        this.erase(p);
        return;
      case 'cropPan': {
        const n = this.doc.get(g.id);
        if (!n) return;
        const local = this.doc.toLocal(n.id, p);
        this.setCrop(n.id, { ...g.crop, x: g.crop.x + local.x - g.start.x, y: g.crop.y + local.y - g.start.y });
        return;
      }
      case 'pathPoint': {
        const n = this.doc.get(g.id);
        if (!n) return;
        const local = this.doc.toLocal(n.id, p);
        setVectorPath(this.editor, n.id, moveAnchorPart(pathCommands(n.path), g.cmd, g.part, local, !e.altKey));
        return;
      }
      case 'connector': {
        const end = this.connectable(p, g.startId ?? undefined);
        app.setUI({ draft: { kind: 'connector', points: [g.startPage], startId: g.startId ?? undefined, endId: end?.id, cursor: p } });
        return;
      }
      case 'endpoint': {
        const n = this.doc.get(g.id);
        if (!n) return;
        const other = g.which === 'connectorStart' ? n.connectorEnd?.nodeId : n.connectorStart?.nodeId;
        const target = this.connectable(p, other);
        this.editor.update(g.id, { [g.which]: target ? { nodeId: target.id, magnet: 'AUTO', x: p.x, y: p.y } : { x: p.x, y: p.y } }, 'Move connector end');
        app.setUI({ dropTargetId: target?.id ?? null });
        return;
      }
      case 'lineEnd': {
        let q = p;
        if (e.shiftKey) q = snapAngle(g.fixed, p);
        const [a, b] = g.which === 0 ? [q, g.fixed] : [g.fixed, q];
        const n = this.doc.get(g.id);
        if (n) this.editor.update(g.id, lineProps(this.parentLocal(n.parent!, a), this.parentLocal(n.parent!, b)), 'Edit line');
        return;
      }
    }
  }

  pointerUp(e: PointerEvent) {
    const app = this.app;
    const s = this.local(e);
    const p = app.screenToPage(s);
    const dg = app.ui.draggingGuide;
    if (dg) {
      const page = this.doc.must(this.editor.pageId);
      const gs = [...(page.guides ?? [])];
      const onRuler = dg.axis === 'x' ? s.y < 20 : s.x < 20;
      if (dg.index >= 0) gs.splice(dg.index, 1);
      if (!onRuler) gs.push({ axis: dg.axis, pos: dg.pos });
      this.editor.update(page.id, { guides: gs.length ? gs : undefined }, onRuler ? 'Remove guide' : 'Move guide');
      app.setUI({ draggingGuide: null });
      return;
    }
    const g = this.gesture;
    this.gesture = null;
    try {
      this.el.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (!g) {
      if (app.tool === 'pen') this.penDragging = false;
      return;
    }
    switch (g.kind) {
      case 'pan':
        this.el.style.cursor = '';
        break;
      case 'pending': {
        // A click without drag
        const before = [...this.editor.selection];
        if (g.marqueeInstead && g.hit) this.editor.setSelection(g.shift ? toggle(this.editor.selection, g.hit.id) : [g.hit.id]);
        else if (g.hit) {
          if (g.shift) this.editor.setSelection(toggle(this.editor.selection, g.hit.id));
          else this.editor.setSelection([g.hit.id]);
        }
        this.handleClickCount(e, s, g.hit, before);
        break;
      }
      case 'marquee':
        app.setUI({ marquee: null });
        break;
      case 'lasso':
      case 'pathLasso':
        app.setUI({ lasso: null });
        break;
      case 'move':
        this.finishMove(g, p);
        break;
      case 'resize':
      case 'rotate':
        this.editor.endGesture();
        app.setUI({ guides: [], dragging: false });
        break;
      case 'create':
        this.finishCreate(g, p);
        break;
      case 'eraser':
      case 'pathPoint':
      case 'pathPoints':
      case 'cropPan':
        this.editor.endGesture();
        break;
      case 'pencil': {
        app.setUI({ draft: null });
        if (g.points.length < 2) break;
        const v = pencilToPath(g.points, app.toolOptions.penWidth);
        if (!v) break;
        const id = this.editor.create('VECTOR', {
          x: v.x,
          y: v.y,
          width: v.width,
          height: v.height,
          path: v.path,
          strokes: [solid(app.toolOptions.penColor)],
          strokeWeight: app.toolOptions.penWidth,
          strokeCap: 'ROUND',
          strokeJoin: 'ROUND',
          strokeAlign: 'CENTER',
          fills: [],
          opacity: app.toolOptions.penOpacity,
          name: app.toolOptions.penOpacity < 1 ? 'Highlight' : 'Drawing',
        });
        this.editor.setSelection([id]);
        break;
      }
      case 'connector': {
        app.setUI({ draft: null });
        const end = this.connectable(p, g.startId ?? undefined);
        const dist = Math.hypot(p.x - g.startPage.x, p.y - g.startPage.y) * app.camera.zoom;
        if (dist < 8 && !end) break;
        const id = this.editor.create('CONNECTOR', {
          connectorType: app.toolOptions.connectorType,
          connectorStart: g.startId ? { nodeId: g.startId, magnet: 'AUTO', x: g.startPage.x, y: g.startPage.y } : { x: g.startPage.x, y: g.startPage.y },
          connectorEnd: end ? { nodeId: end.id, magnet: 'AUTO', x: p.x, y: p.y } : { x: p.x, y: p.y },
        });
        this.editor.setSelection([id]);
        if (!app.toolOptions.keepTool) app.setTool('select');
        break;
      }
      case 'endpoint':
      case 'lineEnd':
        this.editor.endGesture();
        app.setUI({ dropTargetId: null });
        break;
    }
  }

  /** Right-click: select what's under the cursor unless it's already part of the selection. */
  contextSelect(e: MouseEvent) {
    const p = this.app.screenToPage(this.local(e));
    const path = hitTestPath(this.doc, this.editor.pageId, p, { tolerance: this.tol() });
    const sel = new Set(this.editor.selection);
    if (path.some((n) => sel.has(n.id))) return;
    const hit = pickSelectable(this.doc, path, this.app.ui.scopeId);
    this.editor.setSelection(hit ? [hit.id] : []);
  }

  // ------------------------------------------------------------------ select tool

  private selectDown(e: PointerEvent, s: Vec2, p: Vec2) {
    const app = this.app;
    // widgets: clicking an element with an onClick runs it (their rendered layers are locked)
    if (!e.shiftKey && !app.ui.devMode) {
      const wp = hitTestPath(this.doc, this.editor.pageId, p, { includeLocked: true });
      const deep = wp[wp.length - 1];
      if (deep && widgetClick(deep.id)) return;
    }
    // during a vote, clicks place votes (alt-click takes one back)
    const vote = app.voting;
    if (vote?.active && vote.pageId === this.editor.pageId) {
      const path = hitTestPath(this.doc, this.editor.pageId, p);
      const target = [...path].reverse().find((n) => n.parent === this.editor.pageId || this.doc.get(n.parent)?.type === 'SECTION');
      if (target && target.type !== 'SECTION') app.sync?.vote(e.altKey ? { action: 'retract', nodeId: target.id } : { action: 'cast', nodeId: target.id });
      return;
    }
    // Image crop mode: drag pans the image inside its frame
    if (app.ui.croppingId) {
      const n = this.doc.get(app.ui.croppingId);
      const paint = n?.fills.find((f) => f.type === 'IMAGE');
      const local = n ? this.doc.toLocal(n.id, p) : null;
      if (n && paint?.type === 'IMAGE' && paint.crop && local) {
        const nat = app.images.naturalSize(paint.src);
        const W = (nat?.width ?? n.width) * paint.crop.scale,
          H = (nat?.height ?? n.height) * paint.crop.scale;
        const inImage = local.x >= paint.crop.x && local.y >= paint.crop.y && local.x <= paint.crop.x + W && local.y <= paint.crop.y + H;
        if (inImage) {
          this.editor.beginGesture('Crop image');
          this.gesture = { kind: 'cropPan', id: n.id, start: local, crop: { ...paint.crop } };
          return;
        }
      }
      app.setUI({ croppingId: null });
    }
    // Vector edit mode: grab anchors / handles; clicking elsewhere leaves the mode
    if (app.ui.editingPathId) {
      const n = this.doc.get(app.ui.editingPathId);
      if (n) {
        const m = mul(app.viewMatrix(), this.doc.worldTransform(n.id));
        for (const a of anchorsOf(pathCommands(n.path))) {
          for (const part of ['hin', 'hout', 'p'] as const) {
            const pt = part === 'p' ? a.p : a[part];
            if (!pt) continue;
            const sp = apply(m, pt);
            if (Math.hypot(sp.x - s.x, sp.y - s.y) <= 6) {
              if (part !== 'p' && app.ui.pathAnchor !== null && Math.abs(a.cmd - app.ui.pathAnchor) > 1) continue;
              const picked = app.ui.pathAnchors;
              if (part === 'p' && e.shiftKey) {
                const next = picked.includes(a.cmd) ? picked.filter((c) => c !== a.cmd) : [...picked, a.cmd];
                app.setUI({ pathAnchors: next, pathAnchor: next.length === 1 ? next[0] : null });
                return;
              }
              this.editor.beginGesture('Edit path');
              if (part === 'p' && picked.length > 1 && picked.includes(a.cmd)) {
                const wm = this.doc.worldTransform(n.id);
                const anchors = anchorsOf(pathCommands(n.path));
                const starts = picked.flatMap((c) => {
                  const q = anchors.find((x) => x.cmd === c);
                  return q ? [{ cmd: c, page: apply(wm, q.p) }] : [];
                });
                this.gesture = { kind: 'pathPoints', id: n.id, start: p, starts };
                return;
              }
              app.setUI({ pathAnchor: a.cmd, pathAnchors: [a.cmd] });
              this.gesture = { kind: 'pathPoint', id: n.id, cmd: a.cmd, part };
              return;
            }
          }
        }
        const path = hitTestPath(this.doc, this.editor.pageId, p, { tolerance: this.tol() });
        if (path.some((x) => x.id === n.id)) {
          app.setUI({ pathAnchor: null, pathAnchors: [] });
          return;
        }
      }
      app.setUI({ editingPathId: null, pathAnchor: null, pathAnchors: [] });
    }
    // 0. FigJam quick-create: spawn a connected sibling in that direction
    for (const q of quickCreateHandles(app)) {
      if (Math.hypot(q.p.x - s.x, q.p.y - s.y) <= 11) {
        this.quickCreate(q.dir);
        return;
      }
    }
    // a frame's or section's title (above its top-left corner, where the rotate zone also is: the title wins)
    const titled = !e.metaKey && !e.ctrlKey ? titleAt(app, s) : null;
    // 1. Handles of the current selection
    const box = selectionBox(app);
    if (box && !e.metaKey) {
      const single = box.single;
      if (single?.type === 'CONNECTOR') {
        const g = connectorGeometry(this.doc, single);
        const ends = [g.points[0], g.points[g.points.length - 1]];
        for (const [i, which] of (['connectorStart', 'connectorEnd'] as const).entries()) {
          const sp = app.pageToScreen(ends[i]);
          if (Math.hypot(sp.x - s.x, sp.y - s.y) < 8) {
            this.editor.beginGesture('Move connector end');
            this.gesture = { kind: 'endpoint', id: single.id, which };
            return;
          }
        }
      } else if (single?.type === 'LINE') {
        const m = this.doc.worldTransform(single.id);
        const ends = [apply(m, { x: 0, y: 0 }), apply(m, { x: single.width, y: 0 })];
        for (let i = 0; i < 2; i++) {
          const sp = app.pageToScreen(ends[i]);
          if (Math.hypot(sp.x - s.x, sp.y - s.y) < 8) {
            this.editor.beginGesture('Edit line');
            this.gesture = { kind: 'lineEnd', id: single.id, which: i as 0 | 1, fixed: ends[1 - i] };
            return;
          }
        }
      } else {
        const h0 = this.handleAt(s);
        const h = h0 === 'rotate' && titled ? null : h0;
        // double-clicking the edge of a selected vector edits its points (its box hugs the path)
        const quick = performance.now() - this.lastClick.t < 350 && Math.hypot(s.x - this.lastClick.x, s.y - this.lastClick.y) < 6;
        if (h && h !== 'rotate' && quick && single && ['VECTOR', 'POLYGON', 'STAR'].includes(single.type) && !single.locked) return this.doubleClick(single, p, e);
        if (h === 'rotate') return this.startRotate(p);
        if (h) {
          this.lastClick = { t: performance.now(), x: s.x, y: s.y };
          return this.startResize(h, p);
        }
      }
    }
    // 2. A title selects its frame (drag to move it); double-click renames it
    if (titled && !titled.locked) {
      if (app.ui.scopeId && !this.doc.isAncestor(app.ui.scopeId, titled.id)) app.setUI({ scopeId: null });
      if (!e.shiftKey && !this.editor.selection.includes(titled.id)) this.editor.setSelection([titled.id]);
      this.gesture = { kind: 'pending', start: s, startPage: p, hit: titled, marqueeInstead: false, alt: e.altKey, shift: e.shiftKey };
      return;
    }
    // 3. Nodes
    const path = hitTestPath(this.doc, this.editor.pageId, p, { tolerance: this.tol() });
    let hit: NodeData | null = null;
    if (path.length) {
      if (e.metaKey || e.ctrlKey) hit = path[path.length - 1];
      else {
        // keep an existing selection if the click lands inside it (so it can be dragged)
        const sel = new Set(this.editor.selection);
        const inSel = path.find((n) => sel.has(n.id));
        hit = inSel ?? pickSelectable(this.doc, path, app.ui.scopeId) ?? null;
      }
    }
    // leaving the scope when clicking outside it
    if (app.ui.scopeId && (!hit || !this.doc.isAncestor(app.ui.scopeId, hit.id))) app.setUI({ scopeId: null });
    const marqueeInstead = !!hit && isContextContainer(this.doc, hit) && hit.type !== 'SECTION' && !this.editor.selection.includes(hit.id) && this.doc.children(hit.id).length > 0 && hit === path[0] && path.length === 1;
    if (hit && !marqueeInstead && !e.shiftKey && !this.editor.selection.includes(hit.id)) this.editor.setSelection([hit.id]);
    if (!hit && !e.shiftKey) this.editor.setSelection([]);
    this.gesture = { kind: 'pending', start: s, startPage: p, hit, marqueeInstead, alt: e.altKey, shift: e.shiftKey };
  }

  /** Duplicate the selected sticky/shape next to it (skipping occupied space) and connect them. */
  quickCreate(dir: 'N' | 'E' | 'S' | 'W') {
    const src = this.editor.selectedNodes()[0];
    if (!src) return;
    const gap = 80;
    const dx = dir === 'E' ? src.width + gap : dir === 'W' ? -(src.width + gap) : 0;
    const dy = dir === 'S' ? src.height + gap : dir === 'N' ? -(src.height + gap) : 0;
    let x = src.x + dx,
      y = src.y + dy;
    // keep stepping while the slot is occupied by a sibling
    for (let i = 0; i < 20; i++) {
      const hit = this.doc.children(src.parent!).some((k) => k.id !== src.id && k.type !== 'CONNECTOR' && Math.abs(k.x - x) < src.width * 0.5 && Math.abs(k.y - y) < src.height * 0.5);
      if (!hit) break;
      if (dir === 'E' || dir === 'W') y += src.height + 40;
      else x += src.width + 40;
    }
    let id = '';
    this.editor.transact('Quick create', () => {
      id = this.editor.create(src.type, { parent: src.parent!, x, y, width: src.width, height: src.height, fills: structuredClone(src.fills), strokes: structuredClone(src.strokes), shape: src.shape, characters: '', authorName: src.type === 'STICKY' ? this.app.userName : undefined, textColor: src.textColor, fontSize: src.type === 'STICKY' ? 24 : src.fontSize });
      this.editor.create('CONNECTOR', {
        parent: src.parent!,
        connectorType: 'ELBOW',
        connectorStart: { nodeId: src.id, magnet: 'AUTO', x: 0, y: 0 },
        connectorEnd: { nodeId: id, magnet: 'AUTO', x: 0, y: 0 },
      });
      this.editor.setSelection([id]);
    });
    this.app.setUI({ editingTextId: id });
  }

  /** Which selection handle is under screen point s. */
  handleAt(s: Vec2): HandleId | 'rotate' | null {
    const box = selectionBox(this.app);
    if (!box) return null;
    if (box.single && (box.single.type === 'CONNECTOR' || box.single.type === 'LINE')) return null;
    if (box.single?.locked) return null;
    const hs = handlePositions(this.app, box);
    for (const h of hs) if (h.id.length === 2 && Math.hypot(h.p.x - s.x, h.p.y - s.y) <= 6) return h.id;
    // edges
    const corner = (id: HandleId) => hs.find((h) => h.id === id)!.p;
    const edges: [HandleId, HandleId, HandleId][] = [
      ['n', 'nw', 'ne'],
      ['e', 'ne', 'se'],
      ['s', 'se', 'sw'],
      ['w', 'sw', 'nw'],
    ];
    for (const [id, a, b] of edges) if (distToSegment(s, corner(a), corner(b)) <= 4) return id;
    // rotation zones just outside the corners
    for (const h of hs) if (h.id.length === 2) {
      const d = Math.hypot(h.p.x - s.x, h.p.y - s.y);
      if (d > 6 && d < 20 && !this.pointInBox(s, hs)) return 'rotate';
    }
    return null;
  }

  private pointInBox(s: Vec2, hs: { id: HandleId; p: Vec2 }[]) {
    const poly = ['nw', 'ne', 'se', 'sw'].map((id) => hs.find((h) => h.id === id)!.p);
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      if (poly[i].y > s.y !== poly[j].y > s.y && s.x < ((poly[j].x - poly[i].x) * (s.y - poly[i].y)) / (poly[j].y - poly[i].y) + poly[i].x) inside = !inside;
    }
    return inside;
  }

  private hover(e: PointerEvent, s: Vec2, p: Vec2) {
    const app = this.app;
    if (app.tool === 'select' && !app.ui.spaceDown) {
      const h0 = this.handleAt(s);
      const h = h0 === 'rotate' && titleAt(app, s) ? null : h0;
      if (h) {
        this.el.style.cursor = h === 'rotate' ? rotateCursor : resizeCursor(h, selectionBox(app)?.single?.rotation ?? 0);
        if (app.ui.hoverId) app.setUI({ hoverId: null });
        return;
      }
    }
    this.el.style.cursor = app.ui.spaceDown || app.tool === 'hand' ? 'grab' : app.tool === 'select' || app.tool === 'lasso' ? 'default' : app.tool === 'text' ? 'text' : 'crosshair';
    let hoverId: NodeId | null = null;
    if (app.tool === 'select') {
      const path = hitTestPath(this.doc, this.editor.pageId, p, { tolerance: this.tol() });
      const n = e.metaKey || e.ctrlKey ? path[path.length - 1] : (titleAt(app, s) ?? pickSelectable(this.doc, path, app.ui.scopeId));
      hoverId = n?.id ?? null;
    } else if (app.tool === 'connector') {
      hoverId = this.connectable(p)?.id ?? null;
    }
    if (hoverId !== app.ui.hoverId) app.setUI({ hoverId });
  }

  /** The selection before the first click of a double-click (a click narrows a multi-selection). */
  private firstClickSelection: NodeId[] = [];

  private handleClickCount(e: PointerEvent, s: Vec2, hit: NodeData | null, before: NodeId[] = []) {
    const now = performance.now();
    const dbl = now - this.lastClick.t < 350 && Math.hypot(s.x - this.lastClick.x, s.y - this.lastClick.y) < 6;
    this.lastClick = { t: dbl ? 0 : now, x: s.x, y: s.y };
    if (!dbl) this.firstClickSelection = before;
    if (dbl) this.doubleClick(hit, this.app.screenToPage(s), e);
  }

  private doubleClick(hit: NodeData | null, p: Vec2, e: PointerEvent) {
    const app = this.app;
    if (!hit) return;
    if (titleAt(app, app.pageToScreen(p))?.id === hit.id) return app.startRename(hit.id);
    // link cards with a player (YouTube, Figma…) play in place
    const card = [hit, ...this.doc.ancestors(hit.id)].find((n) => n.linkPreview?.embed);
    if (card) {
      app.setUI({ playingEmbedId: card.id });
      return;
    }
    const imgPaint = hit.fills.find((f) => f.type === 'IMAGE');
    if (imgPaint && imgPaint.type === 'IMAGE' && !hit.locked && !this.doc.children(hit.id).length) {
      this.startCrop(hit.id);
      return;
    }
    if (['VECTOR', 'POLYGON', 'STAR'].includes(hit.type) && !hit.locked) {
      let id = hit.id;
      if (hit.type !== 'VECTOR') id = flatten(this.editor, [hit.id])[0] ?? hit.id;
      this.editor.setSelection([id]);
      app.setUI({ editingPathId: id, pathAnchor: null, pathAnchors: [] });
      return;
    }
    if (hit.type === 'TEXT' || hit.type === 'STICKY' || hit.type === 'SHAPE_WITH_TEXT' || hit.type === 'CONNECTOR') {
      if (!hit.locked) {
        this.editor.setSelection([hit.id]);
        app.setUI({ editingTextId: hit.id });
      }
      return;
    }
    // Enter containers: select the child under the pointer
    if (this.doc.children(hit.id).length) {
      const path = hitTestPath(this.doc, this.editor.pageId, p, { tolerance: this.tol() });
      const i = path.findIndex((n) => n.id === hit.id);
      const child = path[i + 1];
      if (child) {
        app.setUI({ scopeId: hit.id });
        // several similar layers selected (a grid of cards): edit them together
        const sel = this.firstClickSelection;
        if (sel.length > 1 && sel.includes(hit.id) && areSimilar(this.doc, sel)) {
          const path = pathIn(this.doc, hit.id, child.id);
          this.editor.multiEdit = [...sel];
          this.editor.setSelection(path ? sel.map((r) => (r === hit.id ? child.id : atPath(this.doc, r, path))).filter((x): x is NodeId => !!x) : [child.id]);
          app.emit('ui');
          return;
        }
        this.editor.setSelection([child.id]);
        if (e.detail > 2) return;
        // double-clicking text deeper inside goes straight to editing
        if (child.type === 'TEXT' && i + 2 >= path.length) app.setUI({ editingTextId: child.id });
      }
    }
  }

  // ------------------------------------------------------------------ move

  private startMove(startPage: Vec2, alt: boolean) {
    const app = this.app;
    let ids = this.editor.topLevel(this.editor.selection).filter((n) => !n.locked).map((n) => n.id);
    if (!ids.length) return;
    this.editor.beginGesture('Move');
    if (alt) ids = this.editor.duplicate(ids, { x: 0, y: 0 });
    let bounds: Rect | null = null;
    for (const id of ids) {
      const b = this.doc.worldBounds(id);
      bounds = bounds ? unionRect(bounds, b) : b;
    }
    const first = this.doc.get(ids[0])!;
    const parent = this.doc.get(first.parent);
    const reorderParent = isAutoLayout(parent) && ids.every((id) => this.doc.get(id)!.parent === parent!.id && this.doc.get(id)!.layoutPositioning !== 'ABSOLUTE') ? parent!.id : null;
    this.gesture = { kind: 'move', startPage, applied: { x: 0, y: 0 }, ids, bounds: bounds!, targets: this.snapTargets(new Set(ids)), reorderParent, grab: { x: startPage.x - bounds!.x, y: startPage.y - bounds!.y } };
    app.setUI({ dragging: true, hoverId: null });
  }

  private moveDrag(g: Extract<Gesture, { kind: 'move' }>, p: Vec2, e: PointerEvent) {
    const app = this.app;
    let dx = p.x - g.startPage.x;
    let dy = p.y - g.startPage.y;
    if (e.shiftKey) {
      if (Math.abs(dx) > Math.abs(dy)) dy = 0;
      else dx = 0;
    }
    // Auto-layout children reorder instead of moving freely while inside their parent.
    if (g.reorderParent) {
      const parent = this.doc.get(g.reorderParent)!;
      const pb = this.doc.worldBounds(parent.id);
      const inside = p.x >= pb.x && p.x <= pb.x + pb.width && p.y >= pb.y && p.y <= pb.y + pb.height;
      if (inside) {
        const slot = this.insertSlot(parent, p, new Set(g.ids));
        app.setUI({ insertLine: slot.line, dropTargetId: null, guides: [] });
        return;
      }
      app.setUI({ insertLine: null });
    }
    if (!e.altKey && !(e.metaKey || e.ctrlKey)) {
      const snapped = this.snap({ ...g.bounds, x: g.bounds.x + dx, y: g.bounds.y + dy }, g.targets);
      dx += snapped.dx;
      dy += snapped.dy;
      app.ui.guides = snapped.guides;
    } else app.ui.guides = [];
    if (g.reorderParent) {
      // dragging out of an auto layout frame: detach to the page under the pointer at drop
      app.setUI({ dropTargetId: this.dropTarget(p, new Set(g.ids)) });
      app.setUI({ marquee: null });
      app.ui.guides = [];
      this.ghost = { ids: g.ids, dx, dy };
      app.requestRender();
      return;
    }
    const ddx = dx - g.applied.x,
      ddy = dy - g.applied.y;
    if (ddx || ddy) {
      this.editor.moveBy(g.ids, ddx, ddy);
      g.applied = { x: dx, y: dy };
    }
    const target = this.dropTarget(p, new Set(g.ids));
    const currentParent = this.doc.get(g.ids[0])?.parent ?? null;
    app.setUI({ dropTargetId: target && target !== currentParent && target !== this.editor.pageId ? target : null });
  }

  /** Ghost offset for auto-layout children dragged out (rendered by the overlay). */
  ghost: { ids: NodeId[]; dx: number; dy: number } | null = null;

  private finishMove(g: Extract<Gesture, { kind: 'move' }>, p: Vec2) {
    const app = this.app;
    const target = this.dropTarget(p, new Set(g.ids)) ?? this.editor.pageId;
    if (g.reorderParent) {
      const parent = this.doc.get(g.reorderParent)!;
      const pb = this.doc.worldBounds(parent.id);
      const inside = p.x >= pb.x && p.x <= pb.x + pb.width && p.y >= pb.y && p.y <= pb.y + pb.height;
      if (inside) {
        const slot = this.insertSlot(parent, p, new Set(g.ids));
        this.editor.transact('Reorder', () => g.ids.forEach((id, i) => this.editor.setZIndex(id, slot.index + i)));
      } else if (this.ghost) {
        // Leave the auto layout: reparent and place at the drop point.
        const gh = this.ghost;
        this.editor.transact('Move out of auto layout', () => {
          for (const id of g.ids) {
            const b = this.doc.worldBounds(id);
            this.editor.reparent(id, target);
            this.editor.moveBy([id], gh.dx + (g.bounds.x - b.x) * 0, gh.dy);
          }
        });
      }
    } else if (target) {
      const ids = g.ids.filter((id) => this.doc.get(id)?.parent !== target);
      if (ids.length) {
        const t = this.doc.get(target)!;
        this.editor.transact('Move into', () => {
          for (const id of ids) {
            const pos = isAutoLayout(t) ? this.insertSlot(t, p, new Set(ids)).index : undefined;
            this.editor.reparent(id, target, pos);
          }
        });
      }
    }
    this.ghost = null;
    this.editor.endGesture();
    app.setUI({ dragging: false, guides: [], dropTargetId: null, insertLine: null });
  }

  /** Where to insert into an auto-layout frame for pointer p. */
  private insertSlot(parent: NodeData, p: Vec2, exclude: Set<NodeId>): { index: number; line: { a: Vec2; b: Vec2 } } {
    const kids = this.doc.children(parent.id).filter((k) => !exclude.has(k.id) && k.visible && k.layoutPositioning !== 'ABSOLUTE');
    const horizontal = parent.layoutMode === 'HORIZONTAL';
    const pb = this.doc.worldBounds(parent.id);
    let index = kids.length;
    for (let i = 0; i < kids.length; i++) {
      const b = this.doc.worldBounds(kids[i].id);
      const mid = horizontal ? b.x + b.width / 2 : b.y + b.height / 2;
      if ((horizontal ? p.x : p.y) < mid) {
        index = i;
        break;
      }
    }
    let pos: number;
    if (!kids.length) pos = horizontal ? pb.x + (parent.paddingLeft ?? 0) : pb.y + (parent.paddingTop ?? 0);
    else if (index === kids.length) {
      const b = this.doc.worldBounds(kids[kids.length - 1].id);
      pos = horizontal ? b.x + b.width + (parent.itemSpacing ?? 0) / 2 : b.y + b.height + (parent.itemSpacing ?? 0) / 2;
    } else {
      const b = this.doc.worldBounds(kids[index].id);
      pos = horizontal ? b.x - (parent.itemSpacing ?? 0) / 2 : b.y - (parent.itemSpacing ?? 0) / 2;
    }
    // convert index among filtered kids to index among all siblings
    const all = this.doc.children(parent.id).filter((k) => !exclude.has(k.id));
    const real = index < kids.length ? all.findIndex((k) => k.id === kids[index].id) : all.length;
    const line = horizontal ? { a: { x: pos, y: pb.y + 4 }, b: { x: pos, y: pb.y + pb.height - 4 } } : { a: { x: pb.x + 4, y: pos }, b: { x: pb.x + pb.width - 4, y: pos } };
    return { index: real, line };
  }

  /** Frame/section under the pointer that dragged nodes would drop into. */
  dropTarget(p: Vec2, exclude: Set<NodeId>): NodeId | null {
    const doc = this.doc;
    const visit = (parentId: NodeId): NodeId | null => {
      const kids = doc.children(parentId);
      for (let i = kids.length - 1; i >= 0; i--) {
        const k = kids[i];
        if (exclude.has(k.id) || !k.visible || k.locked) continue;
        if (!(k.type === 'FRAME' || k.type === 'SECTION' || k.type === 'COMPONENT')) continue;
        const local = apply(invert(doc.worldTransform(k.id)), p);
        if (local.x < 0 || local.y < 0 || local.x > k.width || local.y > k.height) continue;
        return visit(k.id) ?? k.id;
      }
      return null;
    };
    return visit(this.editor.pageId);
  }

  // ------------------------------------------------------------------ snapping

  private snapTargets(exclude: Set<NodeId>) {
    const xs: number[] = [];
    const ys: number[] = [];
    const rects: Rect[] = [];
    const view = this.app.viewportRect();
    const add = (b: Rect) => {
      xs.push(b.x, b.x + b.width / 2, b.x + b.width);
      ys.push(b.y, b.y + b.height / 2, b.y + b.height);
      rects.push(b);
    };
    const first = this.doc.get([...exclude][0]);
    const parentId = first?.parent ?? this.editor.pageId;
    const parent = this.doc.get(parentId);
    if (parent && parent.type !== 'PAGE') add(this.doc.worldBounds(parent.id));
    for (const g of this.doc.get(this.editor.pageId)?.guides ?? []) (g.axis === 'x' ? xs : ys).push(g.pos);
    let count = 0;
    for (const k of this.doc.children(parentId)) {
      if (exclude.has(k.id) || !k.visible || count > 800) continue;
      const b = this.doc.worldBounds(k.id);
      if (b.x > view.x + view.width || b.x + b.width < view.x || b.y > view.y + view.height || b.y + b.height < view.y) continue;
      add(b);
      count++;
    }
    return { xs, ys, rects };
  }

  private snap(r: Rect, t: { xs: number[]; ys: number[]; rects: Rect[] }, edges: { x?: number[]; y?: number[] } = {}): { dx: number; dy: number; guides: Guide[] } {
    const th = 6 / this.app.camera.zoom;
    const mx = edges.x ?? [r.x, r.x + r.width / 2, r.x + r.width];
    const my = edges.y ?? [r.y, r.y + r.height / 2, r.y + r.height];
    let bx: { d: number; v: number } | null = null;
    let by: { d: number; v: number } | null = null;
    for (const a of mx) for (const b of t.xs) {
      const d = b - a;
      if (Math.abs(d) <= th && (!bx || Math.abs(d) < Math.abs(bx.d))) bx = { d, v: b };
    }
    for (const a of my) for (const b of t.ys) {
      const d = b - a;
      if (Math.abs(d) <= th && (!by || Math.abs(d) < Math.abs(by.d))) by = { d, v: b };
    }
    const guides: Guide[] = [];
    const dx = bx?.d ?? 0;
    const dy = by?.d ?? 0;
    const moved = { ...r, x: r.x + dx, y: r.y + dy };
    if (bx) {
      let y0 = moved.y,
        y1 = moved.y + moved.height;
      for (const o of t.rects) if ([o.x, o.x + o.width / 2, o.x + o.width].some((v) => Math.abs(v - bx!.v) < 0.01)) {
        y0 = Math.min(y0, o.y);
        y1 = Math.max(y1, o.y + o.height);
      }
      guides.push({ axis: 'x', pos: bx.v, from: y0, to: y1 });
    }
    if (by) {
      let x0 = moved.x,
        x1 = moved.x + moved.width;
      for (const o of t.rects) if ([o.y, o.y + o.height / 2, o.y + o.height].some((v) => Math.abs(v - by!.v) < 0.01)) {
        x0 = Math.min(x0, o.x);
        x1 = Math.max(x1, o.x + o.width);
      }
      guides.push({ axis: 'y', pos: by.v, from: x0, to: x1 });
    }
    return { dx, dy, guides };
  }

  // ------------------------------------------------------------------ resize & rotate

  private startResize(handle: HandleId, p: Vec2) {
    const box = selectionBox(this.app)!;
    const sel = this.editor.topLevel(this.editor.selection);
    this.editor.beginGesture('Resize');
    this.gesture = {
      kind: 'resize',
      handle,
      startPage: p,
      single: box.single,
      startWorld: box.m,
      box: { x: box.m[4], y: box.m[5], width: box.width, height: box.height },
      starts: sel.map((n) => ({ id: n.id, b: this.doc.worldBounds(n.id), w: n.width, h: n.height })),
    };
    this.app.setUI({ dragging: true });
  }

  private resizeDrag(g: Extract<Gesture, { kind: 'resize' }>, p: Vec2, e: PointerEvent) {
    const h = HANDLES.find((x) => x.id === g.handle)!;
    const alt = e.altKey;
    if (g.single) {
      const n = this.doc.get(g.single.id);
      if (!n) return;
      const w0 = g.single.width,
        h0 = g.single.height;
      const local = apply(invert(g.startWorld), p);
      let nw = w0,
        nh = h0;
      const ax = alt ? 0.5 : 1 - h.u;
      const ay = alt ? 0.5 : 1 - h.v;
      const guides: Guide[] = [];
      if (h.u !== 0.5) nw = Math.abs(local.x - ax * w0) * (alt ? 2 : 1);
      if (h.v !== 0.5) nh = Math.abs(local.y - ay * h0) * (alt ? 2 : 1);
      // Snap edges for unrotated nodes
      if (!g.single.rotation && !e.metaKey) {
        const t = this.snapTargets(new Set([g.single.id]));
        const b0 = this.doc.worldBounds(g.single.id);
        const startB = { x: g.startWorld[4], y: g.startWorld[5] };
        const r = { x: startB.x + (ax * w0 - ax * nw), y: startB.y + (ay * h0 - ay * nh), width: nw, height: nh };
        const xs = h.u === 1 ? [r.x + r.width] : h.u === 0 ? [r.x] : [];
        const ys = h.v === 1 ? [r.y + r.height] : h.v === 0 ? [r.y] : [];
        const s = this.snap(r, t, { x: xs, y: ys });
        if (xs.length) nw += h.u === 1 ? s.dx : -s.dx;
        if (ys.length) nh += h.v === 1 ? s.dy : -s.dy;
        guides.push(...s.guides.filter((gd) => (gd.axis === 'x' ? xs.length : ys.length)));
        void b0;
      }
      const corner = h.u !== 0.5 && h.v !== 0.5;
      if (e.shiftKey && corner && w0 && h0) {
        const s = Math.max(nw / w0, nh / h0);
        nw = w0 * s;
        nh = h0 * s;
      }
      nw = Math.max(1, Math.round(nw * 100) / 100);
      nh = Math.max(n.type === 'LINE' ? 0 : 1, Math.round(nh * 100) / 100);
      this.editor.resize(n.id, nw, nh, { x: ax, y: ay });
      this.app.setUI({ guides });
      return;
    }
    // Multi-selection: scale everything relative to the opposite anchor of the AABB.
    const B = g.box;
    const ax = alt ? B.x + B.width / 2 : B.x + (1 - h.u) * B.width;
    const ay = alt ? B.y + B.height / 2 : B.y + (1 - h.v) * B.height;
    let sx = h.u === 0.5 ? 1 : Math.abs(p.x - ax) / Math.max(1e-6, Math.abs(B.x + h.u * B.width - ax));
    let sy = h.v === 0.5 ? 1 : Math.abs(p.y - ay) / Math.max(1e-6, Math.abs(B.y + h.v * B.height - ay));
    if (e.shiftKey || (h.u !== 0.5 && h.v !== 0.5 && e.shiftKey)) sx = sy = Math.max(sx, sy);
    this.editor.transact('Resize', () => {
      for (const st of g.starts) {
        const nx = ax + (st.b.x - ax) * sx;
        const ny = ay + (st.b.y - ay) * sy;
        this.editor.resize(st.id, Math.max(1, st.w * sx), Math.max(1, st.h * sy));
        this.editor.setPagePosition(st.id, nx, ny);
      }
    });
  }

  private startRotate(p: Vec2) {
    const box = selectionBox(this.app)!;
    const center = apply(box.m, { x: box.width / 2, y: box.height / 2 });
    const sel = this.editor.topLevel(this.editor.selection);
    this.editor.beginGesture('Rotate');
    this.gesture = {
      kind: 'rotate',
      center,
      a0: Math.atan2(p.y - center.y, p.x - center.x),
      starts: sel.map((n) => ({ id: n.id, rotation: n.rotation, center: apply(this.doc.worldTransform(n.id), { x: n.width / 2, y: n.height / 2 }) })),
    };
    this.app.setUI({ dragging: true });
  }

  // ------------------------------------------------------------------ creation

  private parentLocal(parentId: NodeId, p: Vec2): Vec2 {
    const parent = this.doc.get(parentId);
    if (!parent || parent.type === 'PAGE') return p;
    return this.doc.toLocal(parentId, p);
  }

  private createDrag(g: Extract<Gesture, { kind: 'create' }>, p: Vec2, e: PointerEvent) {
    const app = this.app;
    const dist = Math.hypot(p.x - g.startPage.x, p.y - g.startPage.y) * app.camera.zoom;
    if (!g.id && dist < DRAG_THRESHOLD) return;
    if (g.tool === 'line' || g.tool === 'arrow') {
      const q = e.shiftKey ? snapAngle(g.startPage, p) : p;
      const a = this.parentLocal(g.parent, g.startPage);
      const b = this.parentLocal(g.parent, q);
      if (!g.id) {
        this.editor.beginGesture(g.tool === 'arrow' ? 'Draw arrow' : 'Draw line');
        g.id = this.editor.create('LINE', { parent: g.parent, ...lineProps(a, b), strokeWeight: app.pageKind() === 'WHITEBOARD' ? 4 : 1, endCap: g.tool === 'arrow' ? 'ARROW' : 'NONE', strokes: [solid(app.pageKind() === 'WHITEBOARD' ? '#1e1e1e' : '#000000')] });
        this.editor.setSelection([g.id]);
      } else this.editor.update(g.id, lineProps(a, b), 'Draw line');
      return;
    }
    let r = rectFrom(g.startPage, p);
    if (e.shiftKey) {
      const s = Math.max(r.width, r.height);
      r = { x: p.x < g.startPage.x ? g.startPage.x - s : g.startPage.x, y: p.y < g.startPage.y ? g.startPage.y - s : g.startPage.y, width: s, height: s };
    }
    if (e.altKey) r = { x: g.startPage.x - Math.abs(p.x - g.startPage.x), y: g.startPage.y - Math.abs(p.y - g.startPage.y), width: Math.abs(p.x - g.startPage.x) * 2, height: Math.abs(p.y - g.startPage.y) * 2 };
    const tl = this.parentLocal(g.parent, { x: r.x, y: r.y });
    const props = { x: round(tl.x), y: round(tl.y), width: Math.max(1, round(r.width)), height: Math.max(1, round(r.height)) };
    if (!g.id) {
      this.editor.beginGesture('Draw');
      g.id = this.createForTool(g.tool, g.parent, props);
      this.editor.setSelection([g.id]);
    } else this.editor.update(g.id, g.tool === 'text' ? { ...props, textAutoResize: 'HEIGHT' } : props, 'Draw');
  }

  private finishCreate(g: Extract<Gesture, { kind: 'create' }>, p: Vec2) {
    const app = this.app;
    let id = g.id;
    if (!id) {
      // Click: default-sized node centred on the click
      const size = defaultSize(g.tool);
      const c = this.parentLocal(g.parent, p);
      const props = g.tool === 'text' ? { x: c.x, y: c.y - 10 } : { x: round(c.x - size.w / 2), y: round(c.y - size.h / 2), width: size.w, height: size.h };
      if (g.tool === 'line' || g.tool === 'arrow') {
        id = this.editor.create('LINE', { parent: g.parent, x: c.x - 50, y: c.y, width: 100, height: 0, endCap: g.tool === 'arrow' ? 'ARROW' : 'NONE' });
      } else id = this.createForTool(g.tool, g.parent, props);
      this.editor.setSelection([id]);
    }
    this.editor.endGesture();
    const n = this.doc.get(id);
    if (n && (n.type === 'TEXT' || n.type === 'STICKY' || n.type === 'SHAPE_WITH_TEXT')) app.setUI({ editingTextId: id });
    if (!app.toolOptions.keepTool || g.tool === 'text') app.setTool('select');
  }

  /** A vector, line or shape whose outline passes within a few screen pixels of p. */
  private pathNear(p: Vec2): NodeId | null {
    const tol = 6 / this.app.camera.zoom;
    const kinds = new Set(['VECTOR', 'LINE', 'ELLIPSE', 'POLYGON', 'STAR', 'RECTANGLE']);
    let best: NodeId | null = null;
    let bestD = tol;
    this.doc.walk(this.editor.pageId, (n) => {
      if (!n.visible || n.locked) return false;
      if (!kinds.has(n.type)) return;
      const b = this.doc.worldBounds(n.id);
      if (p.x < b.x - tol || p.y < b.y - tol || p.x > b.x + b.width + tol || p.y > b.y + b.height + tol) return;
      const outline = nodeOutline(n);
      if (!outline) return;
      const m = this.doc.worldTransform(n.id);
      for (const poly of flattenPath(outline, 0.5)) {
        const pts = poly.points.map((q) => apply(m, q));
        if (poly.closed) pts.push(pts[0]);
        for (let i = 1; i < pts.length; i++) {
          const d = distToSegment(p, pts[i - 1], pts[i]);
          if (d < bestD) (bestD = d), (best = n.id);
        }
      }
    });
    return best;
  }

  createForTool(tool: App['tool'], parent: NodeId, props: { x: number; y: number; width?: number; height?: number }): NodeId {
    const app = this.app;
    const wb = app.pageKind() === 'WHITEBOARD';
    const map: Partial<Record<App['tool'], NodeType>> = {
      frame: 'FRAME',
      section: 'SECTION',
      rect: 'RECTANGLE',
      ellipse: 'ELLIPSE',
      polygon: 'POLYGON',
      star: 'STAR',
      text: 'TEXT',
      sticky: 'STICKY',
      shape: 'SHAPE_WITH_TEXT',
    };
    const type = map[tool] ?? 'RECTANGLE';
    const extra: Record<string, unknown> = {};
    if (type === 'FRAME') extra.name = `Frame ${this.doc.children(parent).filter((n) => n.type === 'FRAME').length + 1}`;
    if (type === 'STICKY') {
      extra.fills = [solid(STICKY_COLORS[app.toolOptions.stickyColor] ?? STICKY_COLORS.yellow)];
      extra.authorName = app.userName;
    }
    if (type === 'SHAPE_WITH_TEXT') extra.shape = app.toolOptions.shape;
    if (type === 'TEXT') {
      extra.fontSize = wb ? 24 : 16;
      extra.characters = '';
      if (props.width) extra.textAutoResize = 'HEIGHT';
    }
    if (type === 'SECTION') extra.name = `Section ${this.doc.children(parent).filter((n) => n.type === 'SECTION').length + 1}`;
    void makeNode;
    void parseColor;
    return this.editor.create(type, { parent, ...props, ...extra });
  }

  private pickImage(p: Vec2) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,video/mp4,video/webm,video/quicktime';
    input.onchange = async () => {
      const f = input.files?.[0];
      if (f) await insertImage(this.app, f, p);
      this.app.setTool('select');
    };
    input.click();
  }

  // ------------------------------------------------------------------ pen

  private penDown(p: Vec2) {
    const app = this.app;
    const first = this.penPoints[0];
    if (first && this.penPoints.length > 2 && Math.hypot(first.p.x - p.x, first.p.y - p.y) * app.camera.zoom < 8) {
      this.finishPen(true);
      return;
    }
    this.penPoints.push({ p });
    app.setUI({ draft: { kind: 'pen', points: this.penPoints.map((x) => x.p), cursor: p } });
  }

  finishPen(closed = false) {
    const app = this.app;
    const pts = this.penPoints;
    this.penPoints = [];
    app.setUI({ draft: null });
    if (pts.length < 2) return;
    const v = penToPath(pts.map((x) => x.p), closed);
    const id = this.editor.create('VECTOR', {
      name: 'Vector',
      x: v.x,
      y: v.y,
      width: v.width,
      height: v.height,
      path: v.path,
      fills: closed ? [solid('#d9d9d9')] : [],
      strokes: [solid('#000000')],
      strokeWeight: 1,
      strokeAlign: 'CENTER',
    });
    this.editor.setSelection([id]);
    app.setTool('select');
  }

  cancel() {
    if (this.penPoints.length) {
      this.finishPen(false);
      return true;
    }
    if (this.gesture) {
      this.gesture = null;
      this.editor.endGesture();
      this.app.setUI({ marquee: null, guides: [], draft: null, dragging: false, dropTargetId: null, insertLine: null });
      return true;
    }
    return false;
  }

  /** Enter crop mode: convert the image paint to an explicit crop matching what is shown now. */
  startCrop(id: NodeId) {
    const n = this.doc.must(id);
    const i = n.fills.findIndex((f) => f.type === 'IMAGE');
    const paint = n.fills[i];
    if (!paint || paint.type !== 'IMAGE') return;
    if (paint.scaleMode !== 'CROP' || !paint.crop) {
      const nat = this.app.images.naturalSize(paint.src);
      const iw = nat?.width || n.width,
        ih = nat?.height || n.height;
      const s = Math.max(n.width / iw, n.height / ih);
      const crop = { x: (n.width - iw * s) / 2, y: (n.height - ih * s) / 2, scale: s };
      this.editor.update(id, { fills: n.fills.map((f, j) => (j === i ? { ...paint, scaleMode: 'CROP', crop } : f)) }, 'Crop image');
    }
    this.editor.setSelection([id]);
    this.app.setUI({ croppingId: id });
  }

  private setCrop(id: NodeId, crop: { x: number; y: number; scale: number }) {
    const n = this.doc.must(id);
    this.editor.update(id, { fills: n.fills.map((f) => (f.type === 'IMAGE' ? { ...f, scaleMode: 'CROP', crop } : f)) }, 'Crop image');
  }

  /** Eraser: deletes freehand drawings (unfilled vectors) under the pointer. */
  private erase(p: Vec2) {
    const r = Math.max(6, 10 / this.app.camera.zoom);
    const path = hitTestPath(this.doc, this.editor.pageId, p, { tolerance: r });
    const hit = [...path].reverse().find((n) => n.type === 'VECTOR' && !n.fills.some((f) => f.visible !== false));
    if (hit) this.editor.remove([hit.id]);
  }

  /** A node a connector can attach to under point p. */
  private connectable(p: Vec2, exclude?: NodeId): NodeData | null {
    const path = hitTestPath(this.doc, this.editor.pageId, p, { tolerance: this.tol() });
    for (let i = path.length - 1; i >= 0; i--) {
      const n = path[i];
      if (n.type === 'CONNECTOR' || n.id === exclude) continue;
      if (n.type === 'SECTION' && i < path.length - 1) continue;
      return n;
    }
    return null;
  }

  // ------------------------------------------------------------------ wheel

  wheel(e: WheelEvent) {
    e.preventDefault();
    const app = this.app;
    const s = this.local(e);
    // crop mode: scroll scales the image around the pointer
    if (app.ui.croppingId && !(e.ctrlKey || e.metaKey)) {
      const n = this.doc.get(app.ui.croppingId);
      const paint = n?.fills.find((f) => f.type === 'IMAGE');
      if (n && paint?.type === 'IMAGE' && paint.crop) {
        const f = Math.exp(-e.deltaY * 0.005);
        const p = this.doc.toLocal(n.id, app.screenToPage(s));
        const c = paint.crop;
        this.setCrop(n.id, { scale: c.scale * f, x: p.x - (p.x - c.x) * f, y: p.y - (p.y - c.y) * f });
        return;
      }
    }
    if (e.ctrlKey || e.metaKey) {
      const factor = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.01));
      app.zoomAt(factor, s);
    } else {
      const k = e.deltaMode === 1 ? 16 : 1;
      const dx = e.shiftKey && !e.deltaX ? e.deltaY : e.deltaX;
      const dy = e.shiftKey && !e.deltaX ? 0 : e.deltaY;
      app.setCamera({ ...app.camera, x: app.camera.x - dx * k, y: app.camera.y - dy * k });
    }
  }
}

// ---------------------------------------------------------------------- helpers

export async function insertImage(app: App, file: Blob, at?: Vec2) {
  const isVideo = file.type.startsWith('video/');
  let media: { src: string; video?: string; width: number; height: number };
  try {
    media = isVideo ? await app.uploadVideo(file).then((v) => ({ src: v.poster, video: v.video, width: v.width, height: v.height })) : await app.uploadImage(file);
  } catch (e) {
    app.toast((e as Error).message);
    return null;
  }
  const { src, width, height } = media;
  const max = 800;
  const s = Math.min(1, max / Math.max(width, height));
  const w = Math.round(width * s),
    h = Math.round(height * s);
  const c = at ?? app.screenToPage({ x: app.viewportSize.width / 2, y: app.viewportSize.height / 2 });
  const id = app.editor.create('IMAGE', { name: (file as File).name ?? (isVideo ? 'Video' : 'Image'), x: Math.round(c.x - w / 2), y: Math.round(c.y - h / 2), width: w, height: h, fills: [{ type: 'IMAGE', src, scaleMode: 'FILL', ...(media.video ? { video: media.video } : {}) }] });
  app.editor.setSelection([id]);
  return id;
}

function lineProps(a: Vec2, b: Vec2) {
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  const angle = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
  const cx = (a.x + b.x) / 2,
    cy = (a.y + b.y) / 2;
  return { x: round(cx - len / 2), y: round(cy), width: Math.max(0.01, round(len)), height: 0, rotation: normalizeAngle(angle) };
}

function snapAngle(o: Vec2, p: Vec2): Vec2 {
  const a = Math.atan2(p.y - o.y, p.x - o.x);
  const s = Math.round(a / (Math.PI / 4)) * (Math.PI / 4);
  const d = Math.hypot(p.x - o.x, p.y - o.y);
  return { x: o.x + Math.cos(s) * d, y: o.y + Math.sin(s) * d };
}

function defaultSize(tool: App['tool']) {
  switch (tool) {
    case 'sticky':
      return { w: 240, h: 240 };
    case 'shape':
      return { w: 200, h: 200 };
    case 'section':
      return { w: 600, h: 400 };
    case 'frame':
      return { w: 100, h: 100 };
    default:
      return { w: 100, h: 100 };
  }
}

function rectFrom(a: Vec2, b: Vec2): Rect {
  return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), width: Math.abs(b.x - a.x), height: Math.abs(b.y - a.y) };
}

function unionRect(a: Rect, b: Rect): Rect {
  const x = Math.min(a.x, b.x),
    y = Math.min(a.y, b.y);
  return { x, y, width: Math.max(a.x + a.width, b.x + b.width) - x, height: Math.max(a.y + a.height, b.y + b.height) - y };
}

function toggle(list: NodeId[], id: NodeId) {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

function round(v: number) {
  return Math.round(v * 100) / 100;
}

const rotateCursor = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24'%3E%3Cpath d='M6 14a7 7 0 1 1 3 5' fill='none' stroke='white' stroke-width='4'/%3E%3Cpath d='M6 14a7 7 0 1 1 3 5' fill='none' stroke='black' stroke-width='1.5'/%3E%3Cpath d='M3 12l3 3 3-3' fill='none' stroke='black' stroke-width='1.5'/%3E%3C/svg%3E") 12 12, auto`;

function resizeCursor(h: HandleId, rotation: number): string {
  const base: Record<HandleId, number> = { e: 0, se: 45, s: 90, sw: 135, w: 180, nw: 225, n: 270, ne: 315 };
  const a = (((base[h] + rotation) % 180) + 180) % 180;
  const idx = Math.round(a / 45) % 4;
  return ['ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize'][idx];
}

export { mul };
